import { AuthProvider as AuthProviderV2 } from '@/common/auth/authProvider';

// Module-scope so all importers (App.vue visibilitychange handler, axios
// interceptors, future call sites) share one in-flight redirect promise and
// one "session expired" flag. If five Dashboard fetches simultaneously hit a
// 401, we want exactly one Okta redirect.
let signInInFlight: Promise<void> | null = null;
let sessionExpired = false;

const sessionExpiredListeners = new Set<() => void>();

function notifySessionExpired() {
  if (sessionExpired) return;
  sessionExpired = true;
  sessionExpiredListeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.warn('session-expired listener threw:', err);
    }
  });
}

export function triggerSignIn(): Promise<void> {
  if (signInInFlight) return signInInFlight;
  signInInFlight = (async () => {
    try {
      await AuthProviderV2.signIn();
      // signIn() resolves without redirecting when okta-auth-js still sees
      // cached tokens (its first step is an isAuthenticated() check). We only
      // get here after a failed renewal or a backend 401, so those tokens are
      // not usable: surface the session-expired screen, whose button forces a
      // real redirect via retrySignIn(). When signIn() does redirect,
      // isAuthenticated() is false and the page is already navigating away.
      if (await AuthProviderV2.isAuthenticated()) notifySessionExpired();
    } catch (err) {
      // signIn() rejects for popup-blocked, network, misconfig, etc. The
      // page can't redirect, so surface the fallback UI in App.vue.
      console.error('AuthProviderV2.signIn() failed:', err);
      notifySessionExpired();
    } finally {
      // Let the next call retry. Leaving the settled promise here would turn
      // every later attempt (e.g. the session-expired Sign in button) into a
      // silent no-op.
      signInInFlight = null;
    }
  })();
  return signInInFlight;
}

/**
 * Explicit user-initiated sign-in from the session-expired screen. Unlike
 * triggerSignIn(), this guarantees a real Okta round trip: cached tokens are
 * dropped first so signIn() cannot short-circuit on its isAuthenticated()
 * check, and if the redirect still has not happened shortly after, the app
 * hard-reloads to re-run the bootstrap handshake instead of leaving a dead
 * button.
 */
export async function retrySignIn(): Promise<void> {
  // okta-auth-js default token storage. While stale tokens sit here signIn()
  // no-ops, and an expired Okta session can't renew them anyway.
  localStorage.removeItem('okta-token-storage');
  try {
    await AuthProviderV2.signIn();
  } catch (err) {
    console.error('retrySignIn: signIn failed, falling back to reload', err);
  }
  // signInWithRedirect() resolves as navigation starts. If we are still on
  // the page after a beat, the redirect did not happen: re-bootstrap.
  window.setTimeout(() => window.location.reload(), 1500);
}

export async function ensureFreshToken(): Promise<string | null> {
  try {
    const token = await AuthProviderV2.getToken();
    if (!token) {
      void triggerSignIn();
      return null;
    }
    return token;
  } catch (err) {
    console.warn('ensureFreshToken: getToken failed, triggering sign-in', err);
    void triggerSignIn();
    return null;
  }
}

export function isSessionExpired(): boolean {
  return sessionExpired;
}

export function onSessionExpired(fn: () => void): () => void {
  sessionExpiredListeners.add(fn);
  return () => {
    sessionExpiredListeners.delete(fn);
  };
}
